import React from "react";
import i18n from "translation";
import { Box, useTheme } from "@mui/material";
import { queryOps } from "system/query/Query";
import QuerySegmentContainer, { QuerySegmentProps } from "../../common/QuerySegmentContainer";
import { useDroppable } from '@dnd-kit/core';
import { QueryBuilderContainerBox } from "components/queries/queryBuilder/common/QueryBuilderContainerBox";
import { QueryBuilderLabel } from "components/queries/queryBuilder/common/QueryBuilderLabel";
import { QueryBuilderDropTarget } from "components/queries/queryBuilder/common/QueryBuilderDropTarget";
import { colorsAndLabelsByOp } from "system/query/QueryUtility";
import { QueryBranch } from "system/query/QueryBranch";

export interface QueryBranchBuilderProps extends QuerySegmentProps {
  queryBranch: QueryBranch;
}

export default function QueryBranchBuilder(props: QueryBranchBuilderProps): JSX.Element {
  const theme = useTheme();

  const {setNodeRef, isOver, active} = useDroppable({
    id: props.path,
  });

  const queryBranch = props.queryBranch;
  const { borderColor, borderColorTop, backgroundColor, label } = colorsAndLabelsByOp(i18n, theme.colors.namedColors, queryBranch.op);
  let displayLabel = label;

  const isOr = queryBranch.op === queryOps.or,
        isNot = queryBranch.op === queryOps.not;
  if (isOr && queryBranch.quorum && queryBranch.quorum > 1) {
    displayLabel = `${queryBranch.quorum} ${i18n.t("query-form.quorum-or-label")}`;
  }
  if (isNot && queryBranch.quorum && queryBranch.quorum > 1) {
    displayLabel = i18n.t("query-form.quorum-not-label", {quorum: queryBranch.quorum});
  }

  const branchChildren = queryBranch?.values && queryBranch?.values?.map((value, index) => {
    return (
      <QuerySegmentContainer
        key={index}
        depth={props.depth + 1}
        isAddingActive={!!active}
        parent={queryBranch}
        path={`${props.path}.values.${index}`}
        queryInfo={props.queryInfo}
        editSegment={props.editSegment}
        addSegment={props.addSegment}
        inSpan={props.inSpan}
        spanSide={props.spanSide}
        querySegment={value} />
    );
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        position: 'relative',
        p: 0,
        width: '100%',
        "& .MuiBox-root:focus-visible": {
          outline: "none"
        }
      }}
    >
      <Box sx={{position:'relative', p:0, width:'100%'}}>
      <QueryBuilderContainerBox
        path={props.path}
        isActiveDropTarget={isOver}
        draggable={props.path?.length > 0}
        borderColor={borderColor}
        borderColorTop={borderColorTop}
        backgroundColor={backgroundColor}
        onClick={() => props.editSegment(queryBranch, props.parent, props.inSpan, props.spanSide)} >
        <QueryBuilderLabel label={displayLabel} />
        <Box>
          {branchChildren}
        </Box>
        <QueryBuilderDropTarget
          borderColor={borderColor} isOver={isOver}
          hasContentsAbove={!!branchChildren?.length}
          onClick={(event:any) => {
            event?.stopPropagation();
            props.addSegment(queryBranch, props.inSpan, props.spanSide);
          }}
        />
      </QueryBuilderContainerBox>
      </Box>
    </Box>
  );
}
